"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextField = exports.inputLabelProps = exports.getInputProps = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const free_regular_svg_icons_1 = require("@fortawesome/free-regular-svg-icons");
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
const material_1 = require("@mui/material");
const react_1 = require("react");
const fontawesome_icon_1 = require("./fontawesome-icon");
/**
 * 'Freezes' a value when it's falsy, meaning the value will never update to
 * be falsy. Useful for keeping a component the same when animating out
 */
const useFrozenValue = (value) => {
    const [frozenValue, setFrozenValue] = (0, react_1.useState)(value);
    if (value && frozenValue !== value) {
        setFrozenValue(value);
    }
    return frozenValue;
};
const getInputProps = ({ success, variant, error, multiline, autoResize, ...otherProps } = {}) => {
    const { sx: InputPropsSx = [], ...otherInputProps } = otherProps;
    const renderEndAdornment = () => {
        if (error || success) {
            return ((0, jsx_runtime_1.jsx)(material_1.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(fontawesome_icon_1.FontAwesomeIcon, { icon: success ? free_regular_svg_icons_1.faCheckCircle : free_solid_svg_icons_1.faCircleExclamation, sx: ({ palette }) => ({
                        color: success ? palette.blue[70] : palette.red[60],
                    }) }) }));
        }
        return null;
    };
    return {
        sx: [
            ({ palette }) => ({
                [`& .${material_1.outlinedInputClasses.notchedOutline}, &:hover .${material_1.outlinedInputClasses.notchedOutline}`]: {
                    borderColor: error ? palette.red[40] : palette.gray[30],
                },
                ...(multiline &&
                    autoResize && {
                    [`& .${material_1.outlinedInputClasses.input}`]: {
                        resize: "auto",
                    },
                }),
            }),
            ...(Array.isArray(InputPropsSx) ? InputPropsSx : [InputPropsSx]),
        ],
        /** `notched` is only expected for `outlined` variant, passing it for other variants gives a console warning
         * @see https://github.com/mui/material-ui/issues/32550 for context
         */
        ...(variant === "outlined" ? { notched: false } : {}),
        ...otherInputProps,
        endAdornment: error || success ? renderEndAdornment() : otherProps.endAdornment,
    };
};
exports.getInputProps = getInputProps;
exports.inputLabelProps = {
    disableAnimation: true,
    shrink: true,
};
exports.TextField = (0, react_1.forwardRef)(({ helperText, variant = "outlined", sx, InputProps: inputProps = {}, success, error, label, showLabelCornerHint, autoResize, ...textFieldProps }, ref) => {
    const frozenHelperText = useFrozenValue(helperText);
    return ((0, jsx_runtime_1.jsx)(material_1.TextField, { ref: ref, sx: [
            {
                ...(!helperText && {
                    [`.${material_1.formHelperTextClasses.root}`]: {
                        marginTop: 0,
                    },
                }),
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ], variant: variant, ...textFieldProps, error: error, label: label ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [label, showLabelCornerHint && ((0, jsx_runtime_1.jsx)(material_1.Typography, { component: "span", variant: "smallTextLabels", sx: ({ palette }) => ({
                        position: "absolute",
                        right: 0,
                        fontWeight: 400,
                        color: palette.gray[60],
                    }), children: textFieldProps.required ? "Required" : "Optional" }))] })) : null, InputLabelProps: exports.inputLabelProps, InputProps: (0, exports.getInputProps)({
            ...inputProps,
            variant,
            success,
            error,
            autoResize,
            multiline: textFieldProps.multiline,
        }), helperText: (0, jsx_runtime_1.jsx)(material_1.Collapse, { in: !!helperText, children: (0, jsx_runtime_1.jsx)(material_1.Box, { children: frozenHelperText }) }), FormHelperTextProps: {
            ...{ as: "div" },
            error,
            sx: ({ typography, palette }) => ({
                marginLeft: 0,
                mt: 0.75,
                ...typography.smallTextLabels,
                color: error ? palette.red[80] : palette.gray[60],
            }),
        }, minRows: 3 }));
});
exports.TextField.displayName = "TextField";
